import Env from "@/config/environment";

/**
 * The Meta login, in a popup we open ourselves on Meta's dialog.
 *
 * It used to be the JavaScript SDK's `FB.login`, which puts the page's own address in
 * the redirect URI — a back-office URL carrying the tenant id, which Meta refuses to
 * match against the app's registered list, so the code could not be exchanged. The
 * dialog now comes back to one fixed address on the API (`/meta/oauth/callback`), which
 * exchanges the code and posts us a **handle** to finish with.
 *
 * Two kinds of message reach this window while the popup is open, and both are checked
 * by origin:
 *  - from our API's callback page: the outcome, with the handle;
 *  - from facebook.com, WhatsApp only: the account and the number chosen in Embedded
 *    Signup (`WA_EMBEDDED_SIGNUP`). It may never come — the server can read them off the
 *    token — so it is collected when it does, never waited for.
 */

export const META_POPUP_SOURCE = "heyup-meta-oauth";

export class MetaLoginCancelled extends Error {
  constructor() {
    super("Meta sign-in cancelled");
  }
}

export type WhatsAppSession = {
  waba_id: string;
  phone_number_id: string;
  /** The number was kept on the WhatsApp Business app. */
  coexistence: boolean;
};

export type MetaLoginResult = {
  handle: string;
  /** WhatsApp: what the dialog said was chosen, when its message arrived. */
  whatsapp?: WhatsAppSession;
};

const originOf = (url: string): string | null => {
  try {
    return new URL(url, window.location.origin).origin;
  } catch {
    return null;
  }
};

const isFacebook = (origin: string): boolean => {
  try {
    return /(^|\.)facebook\.com$/.test(new URL(origin).hostname);
  } catch {
    return false;
  }
};

const CLOSE_POLL_MS = 500;

/**
 * Opens the popup **now**, synchronously — a `window.open` after an `await` has lost
 * the user gesture and is blocked — and sends it to the dialog once `url` arrives.
 */
export const openMetaLogin = (url: Promise<string>): Promise<MetaLoginResult> => {
  const width = 600;
  const height = 760;
  const popup = window.open(
    "about:blank",
    "heyup-meta-login",
    `width=${width},height=${height},left=${window.innerWidth / 2 - width / 2},top=${window.innerHeight / 2 - height / 2}`,
  );
  if (!popup) return Promise.reject(new Error("popup_blocked"));

  const apiOrigin = originOf(Env.server);

  return new Promise<MetaLoginResult>((resolve, reject) => {
    let whatsapp: WhatsAppSession | undefined;
    let settled = false;

    const finish = (fn: () => void) => {
      if (settled) return;
      settled = true;
      window.clearInterval(timer);
      window.removeEventListener("message", onMessage);
      fn();
    };

    const onMessage = (event: MessageEvent) => {
      if (isFacebook(event.origin)) {
        let payload: {
          type?: string;
          event?: string;
          data?: { waba_id?: string; phone_number_id?: string };
        };
        try {
          payload =
            typeof event.data === "string" ? JSON.parse(event.data) : event.data;
        } catch {
          return;
        }
        if (payload?.type !== "WA_EMBEDDED_SIGNUP") return;
        if (payload.event === "CANCEL") {
          popup.close();
          return finish(() => reject(new MetaLoginCancelled()));
        }
        const { waba_id, phone_number_id } = payload.data ?? {};
        if (waba_id && phone_number_id) {
          whatsapp = {
            waba_id,
            phone_number_id,
            coexistence: payload.event === "FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING",
          };
        }
        return;
      }

      const data = event.data;
      if (!data || data.source !== META_POPUP_SOURCE) return;
      // The callback of another deployment finished the login in *its* database.
      if (event.origin !== apiOrigin) {
        return finish(() =>
          reject(new Error(`meta_wrong_origin:${event.origin}`)),
        );
      }
      if (!data.success || !data.handle) {
        return finish(() => reject(new Error(data.error || "meta_login_failed")));
      }
      finish(() => resolve({ handle: data.handle, whatsapp }));
    };

    // A window closed without a word: given up on, or refused on Meta's own page.
    const timer = window.setInterval(() => {
      if (popup.closed) finish(() => reject(new MetaLoginCancelled()));
    }, CLOSE_POLL_MS);

    window.addEventListener("message", onMessage);

    url
      .then((target) => {
        if (settled) return;
        popup.location.href = target;
      })
      .catch((error: unknown) => {
        popup.close();
        finish(() =>
          reject(error instanceof Error ? error : new Error("meta_url_failed")),
        );
      });
  });
};
